import React, { useEffect, useState } from 'react';
import { X, WarningCircle, ArrowCounterClockwise } from '@phosphor-icons/react';

// Confirm dialog for reopening the latest finalized week. Admin types a
// required reason that is stored in session_events.metadata.reason.
export default function ReopenWeekDialog({
  open,
  weekNumber,
  nextWeekExists,
  sessionWasCompleted,
  busy,
  onCancel,
  onConfirm,
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!open) return null;

  const trimmed = reason.trim();
  const ready = trimmed.length >= 3 && !busy;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onCancel}
      data-testid="reopen-week-overlay"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); if (ready) onConfirm(trimmed); }}
        className="w-full max-w-md bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-5 my-8 space-y-4"
        data-testid="reopen-week-dialog"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2">
            <ArrowCounterClockwise size={22} className="text-[#015e3f] mt-1" weight="bold" />
            <div>
              <h3 className="font-serif text-2xl text-[#013523]">Reopen Week {weekNumber}?</h3>
              <p className="text-xs text-[#6a766e] mt-0.5">For corrections only.</p>
            </div>
          </div>
          <button onClick={onCancel} type="button" className="text-[#6a766e]" data-testid="reopen-close">
            <X size={20} />
          </button>
        </div>

        <ul className="text-xs text-[#013523] bg-[#f0ece3] rounded-lg p-3 space-y-1.5 list-disc list-inside">
          <li>This will reopen the latest finalized week for edits.</li>
          {nextWeekExists && (
            <li>Next-week courts auto-generated from this finalize will be removed (no real data lost).</li>
          )}
          {sessionWasCompleted && (
            <li>The session will flip from <strong>completed</strong> back to <strong>in progress</strong>.</li>
          )}
          <li>You must re-save and re-finalize before standings stabilize.</li>
          <li>Older weeks (1 … {weekNumber - 1}) remain locked.</li>
        </ul>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2">
          <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
          <span>Standings will be reverted to before Week {weekNumber}. Re-finalize to apply corrected scores.</span>
        </div>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">
            Reason <span className="text-red-600">*</span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            autoFocus
            required
            placeholder="e.g. Correcting Daniel Park's score from 26 to 28."
            data-testid="reopen-reason-input"
            className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm text-[#013523]"
          />
          <span className="block text-[10px] text-[#6a766e] mt-1">
            Stored in the audit log. {trimmed.length}/500
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-[#e3dcd1] text-[#013523] text-sm"
            data-testid="reopen-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!ready}
            className="px-4 py-2 rounded-lg bg-[#015e3f] text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            data-testid="reopen-confirm-btn"
          >
            <ArrowCounterClockwise size={14} weight="bold" />
            {busy ? 'Reopening…' : `Reopen Week ${weekNumber}`}
          </button>
        </div>
      </form>
    </div>
  );
}
